<script setup lang="ts">
import { type Toast } from '@/models/Toast';

import { watchEffect } from 'vue';

import { DEFAULT_TOAST_TIMEOUT_MS } from '@/utils/constants';

import GIcon from '@/common-components/GIcon';

const props = defineProps<{
  toast: Toast | null;
}>();

const emit = defineEmits<{
  close: [];
}>();

const handleClose = () => {
  emit('close');
};

watchEffect(() => {
  if (props.toast) {
    setTimeout(handleClose, props.toast.timeout_ms ?? DEFAULT_TOAST_TIMEOUT_MS);
  }
});
</script>

<template>
  <Transition name="toast">
    <div v-if="toast" class="g-toast">
      <div class="icon">
        <GIcon icon="circle-info" aria-hidden="true" />
      </div>
      <div class="text">
        <div class="title">{{ toast.title }}</div>
        <div v-if="toast.message" class="description">{{ toast.message }}</div>
      </div>
      <button class="close-button" @click="handleClose">
        <GIcon icon="xmark-large" :title="$t(`actions.close`)" />
      </button>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.g-toast {
  display: flex;
  position: fixed;
  top: 2rem;
  right: 2rem;
  border-radius: var(--radius-medium);
  width: 100%;
  max-width: 400px;
  padding: var(--spacing-5);
  gap: var(--spacing-2);
  z-index: 999999;
  box-shadow: var(--elevation2);
  background-color: var(--white);
}

.icon {
  & > svg {
    fill: var(--color-brand-primary);
  }
}

.text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  line-height: 1rem;

  .title {
    font-weight: var(--font-weight-medium);
  }

  .description {
    font-size: var(--font-size-text-small);
    color: var(--grey-60);
  }
}

.close-button {
  background-color: transparent;
  cursor: pointer;
  border: none;
}

.icon,
.close-button {
  width: 1.25rem;
  height: 1.25rem;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(2rem);
}

@media (prefers-reduced-motion) {
  .toast-enter-active,
  .toast-leave-active {
    transition: none;
  }
}
</style>
